import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpAuthorizationHeader } from '../misc/httpauthorizationheader';
import { withFirstTeamIds } from '../misc/rxjs-operators';
import { BLService } from './bl.service';
import {
  NotificationDataType,
  NotificationResult,
  NotificationResultDto,
  NotificationResultDtoData,
  NotificationResultDtoSkv,
  NotificationResultDtoTodo,
} from './notification.service.types';

const STATUSTEXT_FOR_MISSING_PERMISSION = 'Rättighet saknas';
const STATUSTEXT_FOR_SERVER_ERROR = 'Det gick inte att hämta uppgifterna';
const STATUSTEXT_FOR_MISSING_PROXY = 'Ombudsbehörighet saknas';

interface StatusHandler {
  canHandle: (data?: NotificationResultDtoSkv) => boolean;
  handle: (data?: NotificationResultDtoSkv) => { TaxAccount: string } | null;
}

interface NotificationCache {
  responsibleUserId: number;
  data: NotificationResult[];
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService extends BLService {
  private options = new HttpAuthorizationHeader('application/json');
  // private RESOURCE_ENDPOINT = 'cloudnotification';
  private RESOURCE_ENDPOINT_V2 = 'cloud-company/notifications';
  // private url = environment.serverUrl + this.RESOURCE_ENDPOINT;
  private url_V2 = environment.serverUrlV2 + this.RESOURCE_ENDPOINT_V2;
  private notificationCacheSubject: BehaviorSubject<NotificationCache> = new BehaviorSubject<NotificationCache>(null);
  private handlers: StatusHandler[];

  constructor() {
    super();

    this.handlers = [
      { canHandle: (data) => !data, handle: () => null },
      { canHandle: (data) => !!data?.saldo, handle: (data) => ({ TaxAccount: `${data.saldo.saldoSkatteverket}` }) },
      {
        canHandle: (data) => data?.status?.includes(STATUSTEXT_FOR_MISSING_PERMISSION),
        handle: () => ({ TaxAccount: 'MISSING_PERMISSION' }),
      },
      {
        canHandle: (data) => data?.status?.includes(STATUSTEXT_FOR_SERVER_ERROR),
        handle: () => ({ TaxAccount: 'SERVER_ERROR' }),
      },
      {
        canHandle: (data) => data?.status?.includes(STATUSTEXT_FOR_MISSING_PROXY),
        handle: () => ({ TaxAccount: 'MISSING_PROXY' }),
      },
    ];
  }

  // eslint-disable-next-line max-lines-per-function
  loadTodoInformationForResponsibleUser(
    responsibleUserId: string | number,
    startDate: string,
    endDate: string,
    force = false,
  ): Observable<NotificationResult[]> {
    if (force) {
      this.notificationCacheSubject.next(null);
    }

    const internalResponsibleUserId = this.toNumber(responsibleUserId);

    if (this.hasCacheValue(internalResponsibleUserId)) {
      return this.notificationCacheSubject.asObservable().pipe(
        map((cache) => cache.data),
        take(1),
      );
    }

    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.http
          .get<any>(
            `${this.url_V2}/${internalResponsibleUserId}/${startDate}/${endDate}`,
            this.options.getAuthorizationHeaderWithEmptyBody(ids),
          )
          .pipe(
            map((result: NotificationResultDto[]) => {
              return (result || []).map((item) => {
                if (!item) {
                  return item;
                }

                const { todos, taxAccountBalance, cloudApiKey } = item;
                const data: NotificationDataType = {
                  ...this.mapCompanyTodoInformation(todos),
                  ...this.mapSkvInformation(taxAccountBalance?.data),
                };
                return { data, cloudApiKey, success: Boolean(todos) } as NotificationResult;
              });
            }),
            tap((result) =>
              this.notificationCacheSubject.next({
                responsibleUserId: internalResponsibleUserId,
                data: result,
                timestamp: Date.now(),
              }),
            ),
            this.catchErrorAndShowMessage(),
          ),
      ),
    );
  }

  getLastUpdatedDate() {
    const cacheValue = this.notificationCacheSubject.getValue();
    return cacheValue ? new Date(cacheValue.timestamp) : null;
  }

  private hasCacheValue(responsibleUserId: number) {
    const cacheValue: NotificationCache = this.notificationCacheSubject.getValue();
    if (!cacheValue) {
      return false;
    }
    const hasDataValue = Array.isArray(cacheValue.data) && cacheValue.data.length > 0;
    return cacheValue.responsibleUserId === responsibleUserId && hasDataValue;
  }

  private mapCompanyTodoInformation(
    todos?: NotificationResultDtoTodo[],
  ): Record<keyof NotificationResultDtoData, number> {
    if (!todos) {
      return {} as Record<keyof NotificationResultDtoData, number>;
    }

    return todos.reduce(
      (acc, todo) => {
        acc[this.toPascalCase(todo.type)] = todo.number;
        return acc;
      },
      {} as Record<keyof NotificationResultDtoData, number>,
    );
  }

  private mapSkvInformation(data?: NotificationResultDtoSkv) {
    const handler = this.handlers.find((h) => h.canHandle(data));
    if (!handler) {
      return null;
    }

    return handler.handle(data);
  }

  private toPascalCase(value: string): string {
    const capitalize = (str: string) =>
      ` ${str}`.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_match, chr) => chr.toUpperCase());
    return value.split('_').map(capitalize).join('');
  }

  private toNumber(value: string | number): number {
    if (typeof value === 'number') {
      return value;
    }
    const parsed = parseInt(value, 10);
    if (isNaN(parsed)) {
      return null;
    }
    return parsed;
  }
}
